import { createAction } from '@reduxjs/toolkit';
import * as ActionTypes from './types';

export interface ClaimApplicationType {
    id: string;
    code: string;
    description: string;
    name: string;
    ClaimApplicationState: string;
}

export interface Answers {
    Amount: number | null;
    Date: string | null;
    Flag: boolean | string | null;
    OptionId: string | boolean | null;
    QuestionCode: string;
    SelectedUserId: null | string;
    Text: string;
    amount: number | null;
    date: string | null;
    flag: boolean | string | null;
    optionId: string | boolean | null;
    questionCode: string;
    selectedUserId: null | string;
    text: string;
}

export interface DraftClaimApplicationPayload {
    PolicyTypeCode: string;
    ClaimCategoryCode: string;
    FormTypeCode: Array<string> | string;
    ClaimApplicationId: string;
    Answers: Array<any>;
}

export interface ClaimTransitionStatusReasons {
    claimStatusReasonCode: string;
    claimStatusReasonName: string;
    claimTransitionId: string;
    claimTransitionStatusReasonOptions: Array<string>;
    deletedId: string;
}

export interface ClaimApplicationPayload {
    data:
        | {
              Answers: Array<Answers>;
              ClaimCategoryCode: string;
              ClaimTypeCode: string;
              PolicyTypeCode: string;
              AssigneeId: string;
              claimApplicationId: string;
              claimApplicationTemplate: Array<string> | string | null;
              claimCategoryCode: string;
              claimStateCode: string;
              claimStateMoreInfo: undefined;
              claimStateReasonCodes: Array<string>;
              claimStateReasonOptionCodes: Array<string>;
              claimStatusCode: string;
              claimStatusMoreInfo: undefined;
              claimStatusReasonCodes: string;
              claimStatusReasonOptionCodes: Array<string>;
              claimTypeCode: string;
              isBankDetailsTabVisible: boolean;
              isChallengedTabEditable: boolean;
              policyTypeCodes: string;
              sendEmailToClaimant: boolean;
              visibleToAragOnly: boolean;
              ClaimApplicationId: string | undefined;
              assigneeId: string;
              claimReferenceLike: string | null;
              claimTypesCodes: string | null;
              claimantNameLike: string | null;
              limit: number;
              page: number;
              policyHolderNameLike: null | string;
              submissionDateEnd: string;
              submissionDateStart: string;
              claimTransitionStateReasons: Array<string>;
              claimTransitionStatusReasons: Array<ClaimTransitionStatusReasons>;
              id: string;
              settlementStageNumber: string;
          }
        | string
        | Array<any>;
    key: string;
    value: string;
}

export interface ClaimApplication {
    claimApplicationId: string;
    content: string;
    answers: Array<Answers>;
    assignee: {
        displayName: string;
        email: string;
        firstName: string;
        fullName: string;
        id: string;
        isClaimant: boolean;
        isPolicyHolder: boolean;
        isSystem: boolean;
        lastName: string;
    };
    assigneeId: string;
    claimApplicationApproval: string;
    claimCategory: { id: string; code: string; name: string };
    claimChallengeReviewerId: string;
    claimDetailsMessage: string;
    claimReference: string;
    claimTransition: {
        id: string;
        assignee: string | null;
        assigneeId: string;
        claimState: Array<ClaimApplicationType>;
        claimStatus: {
            code: string;
            description: string;
            id: string;
            name: string;
        };
        claimTransitionStateReasons: Array<string>;
        claimTransitionStatusReasons: Array<ClaimTransitionStatusReasons>;
        importantNotes: string | null;
    };
    claimType: {
        id: string;
        code: string;
        name: string;
    };
    claimant: {
        claimantName: string;
        displayName: string;
        email: string | null;
        firstName: string;
        fullName: string;
        id: string;
        isClaimant: boolean;
        isPolicyHolder: boolean;
        isSystem: boolean;
        lastName: string;
    };
    claimantId: string;
    claimantName: string;
    communicationType: null | string;
    communicationTypeNumber: number;
    communicationTypeMessage: string;
    createdDate: string;
    dateCoverStartedAnswer: string | null;
    dateFormSubmittedAnswer: string | null;
    dateReportedAnswer: string | null;
    entityId: string | null;
    id: string;
    isBankDetailsVisible: boolean;
    isChallengedTabEditable: boolean;
    isChallengedTabVisible: boolean;
    isDraft: boolean;
    isWithdrawn: boolean;
    organisationNameAnswer: string;
    permissionsForCurrentUser: {
        isClaimHandler: boolean;
        isClaimReviewer: boolean;
    };
    policyHolder: null | string;
    policyHolderName: string;
    policyId: null | string;
    policyNumber: null | number;
    policyNumberAnswer: null | number;
    policyType: { id: string; code: string; name: string };
    receivedType: null | string;
    receivedTypeNumber: number;
    receivedTypeMessage: string;
    requiresAcceptance: boolean;
    currency?: null | string;
}

export interface GeneralResponse {
    data: ClaimApplication;
    page: number;
    limit: number;
    totalPages: number;
    count: number;
    totalCount: number;
}

export interface ErrorResponse {
    body: {
        errors: Array<string>;
    };
}

export const sendClaimApplication = createAction<sendClaimApplicationAction>(
    ActionTypes.SEND_CLAIM_APPLICATION,
);
export const sendClaimApplicationSuccess =
    createAction<sendClaimApplicationSuccessAction>(
        ActionTypes.SEND_CLAIM_APPLICATION_SUCCESS,
    );
export const sendClaimApplicationFailed =
    createAction<sendClaimApplicationFailedAction>(
        ActionTypes.SEND_CLAIM_APPLICATION_FAILED,
    );
export const updateClaimApplication =
    createAction<updateClaimApplicationAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION,
    );
export const updateClaimApplicationSuccess =
    createAction<updateClaimApplicationSuccessAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION_SUCCESS,
    );
export const updateClaimApplicationFailed =
    createAction<updateClaimApplicationFailedAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION_FAILED,
    );
export const fetchClaimApplicationById = createAction<
    fetchClaimApplicationByIdAction | string
>(ActionTypes.GET_CLAIM_APPLICATION_BY_ID);
export const fetchClaimApplicationByIdSuccess =
    createAction<fetchClaimApplicationByIdSuccessAction>(
        ActionTypes.GET_CLAIM_APPLICATION_BY_ID_SUCCESS,
    );
export const fetchClaimApplicationByIdFailed =
    createAction<fetchClaimApplicationByIdFailedAction>(
        ActionTypes.GET_CLAIM_APPLICATION_BY_ID_FAILED,
    );
export const fetchClaimApplications =
    createAction<fetchClaimApplicationsAction>(
        ActionTypes.GET_CLAIM_APPLICATIONS,
    );
export const fetchClaimApplicationsSuccess =
    createAction<fetchClaimApplicationsSuccessAction>(
        ActionTypes.GET_CLAIM_APPLICATIONS_SUCCESS,
    );
export const fetchClaimApplicationsFailed =
    createAction<fetchClaimApplicationsFailedAction>(
        ActionTypes.GET_CLAIM_APPLICATIONS_FAILED,
    );
export const changeClaimApplicationState =
    createAction<changeClaimApplicationStateAction>(
        ActionTypes.CHANGE_CLAIM_APPLICATION_STATE,
    );
export const redirectAfterClaimSubmit =
    createAction<redirectAfterClaimSubmitAction>(
        ActionTypes.REDIRECT_AFTER_CLAIM_SUBMIT,
    );
export const sendDraftClaimApplicationByUser =
    createAction<sendDraftClaimApplicationByUserAction>(
        ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER,
    );
export const sendDraftClaimApplicationByUserSuccess =
    createAction<sendDraftClaimApplicationByUserSuccessAction>(
        ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_SUCCESS,
    );
export const sendDraftClaimApplicationByUserFailed =
    createAction<sendDraftClaimApplicationByUserFailedAction>(
        ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_FAILED,
    );
export const fetchClaimApplicationBankAccountAnswers =
    createAction<fetchClaimApplicationBankAccountAnswersAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS,
    );
export const fetchClaimApplicationBankAccountAnswersSuccess =
    createAction<fetchClaimApplicationBankAccountAnswersSuccessAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_SUCCESS,
    );
export const fetchClaimApplicationBankAccountAnswersFailed =
    createAction<fetchClaimApplicationBankAccountAnswersFailedAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_FAILED,
    );
export const updateClaimApplicationBankAccount =
    createAction<updateClaimApplicationBankAccountAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT,
    );
export const updateClaimApplicationBankAccountSuccess =
    createAction<updateClaimApplicationBankAccountSuccessAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_SUCCESS,
    );
export const updateClaimApplicationBankAccountFailed =
    createAction<updateClaimApplicationBankAccountFailedAction>(
        ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_FAILED,
    );
export const createWithdrawnClaimApplication =
    createAction<createWithdrawnClaimApplicationAction>(
        ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION,
    );
export const createWithdrawnClaimApplicationSuccess =
    createAction<createWithdrawnClaimApplicationSuccessAction>(
        ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_SUCCESS,
    );
export const createWithdrawnClaimApplicationFailed =
    createAction<createWithdrawnClaimApplicationFailedAction>(
        ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_FAILED,
    );
export const fetchClaimApplicationTemplate =
    createAction<fetchClaimApplicationTemplateAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE,
    );
export const fetchClaimApplicationTemplateSuccess =
    createAction<fetchClaimApplicationTemplateSuccessAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_SUCCESS,
    );
export const fetchClaimApplicationTemplateFailed =
    createAction<fetchClaimApplicationTemplateFailedAction>(
        ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_FAILED,
    );

export interface sendClaimApplicationAction {
    type?: typeof ActionTypes.SEND_CLAIM_APPLICATION;
    data: {
        ReCaptchaResponseToken: string | null;
        PolicyTypeCode: string;
        ClaimCategoryCode: string;
        FormTypeCode: string;
        Answers: Array<any>;
    };
}

export interface sendClaimApplicationSuccessAction {
    type: typeof ActionTypes.SEND_CLAIM_APPLICATION_SUCCESS;
    payload: GeneralResponse;
}

export interface sendClaimApplicationFailedAction {
    type: typeof ActionTypes.SEND_CLAIM_APPLICATION_FAILED;
    payload: GeneralResponse;
}

export interface updateClaimApplicationAction {
    type?: typeof ActionTypes.UPDATE_CLAIM_APPLICATION;
    data: {
        ReCaptchaResponseToken: string | null;
        PolicyTypeCode: string;
        ClaimCategoryCode: string;
        FormTypeCode: string;
        Answers: Array<any>;
        ClaimApplicationId: string;
    };
}

export interface updateClaimApplicationSuccessAction {
    type: typeof ActionTypes.UPDATE_CLAIM_APPLICATION_SUCCESS;
    payload: GeneralResponse;
}

export interface updateClaimApplicationFailedAction {
    type: typeof ActionTypes.UPDATE_CLAIM_APPLICATION_FAILED;
    payload: GeneralResponse;
}

export interface fetchClaimApplicationByIdAction {
    type?: typeof ActionTypes.GET_CLAIM_APPLICATION_BY_ID;
    id: string;
}

export interface fetchClaimApplicationByIdSuccessAction {
    type: typeof ActionTypes.GET_CLAIM_APPLICATION_BY_ID_SUCCESS;
    body: GeneralResponse;
}

export interface fetchClaimApplicationByIdFailedAction {
    type: typeof ActionTypes.GET_CLAIM_APPLICATION_BY_ID_FAILED;
    payload: GeneralResponse;
}

export interface fetchClaimApplicationsAction {
    type?: typeof ActionTypes.GET_CLAIM_APPLICATIONS;
    page: number;
    limit: number;
}

export interface fetchClaimApplicationsSuccessAction {
    type: typeof ActionTypes.GET_CLAIM_APPLICATIONS_SUCCESS;
    body: GeneralResponse;
}

export interface fetchClaimApplicationsFailedAction {
    type: typeof ActionTypes.GET_CLAIM_APPLICATIONS_FAILED;
    response: ErrorResponse;
}

export interface changeClaimApplicationStateAction {
    type: typeof ActionTypes.CHANGE_CLAIM_APPLICATION_STATE;
    key: string;
    value: string;
}

export interface redirectAfterClaimSubmitAction {
    type: typeof ActionTypes.REDIRECT_AFTER_CLAIM_SUBMIT;
    payload: ClaimApplicationPayload;
}

export interface sendDraftClaimApplicationByUserAction {
    type?: typeof ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER;
    data: {
        PolicyTypeCode: string;
        ClaimCategoryCode: string;
        FormTypeCode: string;
        ClaimApplicationId: string;
        Answers: Array<any>;
    };
}

export interface sendDraftClaimApplicationByUserSuccessAction {
    type: typeof ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_SUCCESS;
    payload: GeneralResponse;
}

export interface sendDraftClaimApplicationByUserFailedAction {
    type: typeof ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_FAILED;
    payload: GeneralResponse;
}

export interface fetchClaimApplicationBankAccountAnswersAction {
    type: typeof ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS;
    payload: string;
}

export interface fetchClaimApplicationBankAccountAnswersSuccessAction {
    type: typeof ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_SUCCESS;
    body: GeneralResponse;
}

export interface fetchClaimApplicationBankAccountAnswersFailedAction {
    type: typeof ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_FAILED;
}

export interface updateClaimApplicationBankAccountAction {
    type: typeof ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT;
    payload: {
        claimApplicationId: string;
        answers: Array<any>;
    };
}

export interface updateClaimApplicationBankAccountSuccessAction {
    type: typeof ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_SUCCESS;
}

export interface updateClaimApplicationBankAccountFailedAction {
    type: typeof ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_FAILED;
}

export interface createWithdrawnClaimApplicationAction {
    type?: typeof ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION;
    data: {
        Answers: Array<any>;
    };
    claimApplicationId: string;
}

export interface createWithdrawnClaimApplicationSuccessAction {
    type: typeof ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_SUCCESS;
    body: GeneralResponse;
}

export interface createWithdrawnClaimApplicationFailedAction {
    type: typeof ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_FAILED;
}

export interface fetchClaimApplicationTemplateAction {
    type?: typeof ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_SUCCESS;
    templateId: string;
    claimApplicationId: string;
}

export interface fetchClaimApplicationTemplateSuccessAction {
    type: typeof ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_SUCCESS;
    body: GeneralResponse;
}

export interface fetchClaimApplicationTemplateFailedAction {
    type: typeof ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_FAILED;
}

export type ClaimApplicationProjections = {
    id: string | null;
    formattedType: string | null;
    claimHandlerName: string | null;
    claimStatusName: string | null;
    claimStateName: string | null;
    createdDate: string | null;
    isBankDetailsVisible: boolean | null;
    isSurveyTabVisible: boolean | null;
};
